/**
 * Explicit ordering bridge between AppContainer's beforeunload handler — which stops
 * the Datadog RUM session (pre-existing behavior) — and journey completions that must
 * be recorded first. stopSession() synchronously closes the RUM session context and
 * the SDK silently drops any action stamped after the close, and beforeunload precedes
 * pagehide on desktop unloads, so a pagehide-only PROCESS_KILLED completion would be
 * deterministically lost there. Listener registration order can't fix this:
 * AppContainer's mount-time listener always registers before the journey-gated anchor
 * effect, and same-target listeners fire in registration order.
 */
type BeforeRumSessionStopCallback = () => void

const callbacks = new Set<BeforeRumSessionStopCallback>()

/** Register a callback to run before the RUM session is stopped; returns an unsubscribe. */
export const onBeforeRumSessionStop = (callback: BeforeRumSessionStopCallback): (() => void) => {
  callbacks.add(callback)
  return () => {
    callbacks.delete(callback)
  }
}

/** Called by the unload path immediately before stopDatadogSession(). */
export const notifyBeforeRumSessionStop = (): void => {
  callbacks.forEach((callback) => {
    try {
      callback()
    } catch {
      // Instrumentation must never break the unload path or starve later callbacks
    }
  })
}
